import type { AxisSelection } from '@atlas/types'
import { toggleFilterValue } from '@atlas/ui-components'
import { useCallback, useMemo, useState } from 'react'
import {
	ARTICLE_LIST_PAGE_SIZE,
	ARTICLE_STATUS_LABELS,
	ARTICLE_TYPE_LABELS,
	type ArticleListParams,
	type ArticleStatus,
	type ArticleType,
} from '@/domain/article.types'
import { DEFAULT_ARTICLE_FILTER_AXES } from '@/hooks/useArticleFilterRail'

/** Sort orders the API actually supports. Anything else it discards. */
export const ARTICLE_SORTS = [
	{ value: 'updatedAt', label: 'Recently updated' },
	{ value: 'publishedAt', label: 'Recently published' },
	{ value: 'title', label: 'Title' },
] as const

export type ArticleSort = (typeof ARTICLE_SORTS)[number]['value']

/** Toggle a value in a flat enum selection. */
function toggled<T>(selection: readonly T[], value: T): T[] {
	return selection.includes(value) ? selection.filter((entry) => entry !== value) : [...selection, value]
}

interface FilterState {
	search: string
	typeSelection: readonly ArticleType[]
	statusSelection: readonly ArticleStatus[]
	classification: AxisSelection
	sort: ArticleSort
}

/**
 * What is narrowing the list, in words.
 *
 * Serves two jobs at once on purpose: it decides WHICH empty state applies (any
 * entry means the empty result is a filtered one) and it names the narrowing inside
 * that state. One derivation, so the two can never disagree.
 */
function describeActiveFilters({ search, typeSelection, statusSelection, classification }: FilterState): string[] {
	const active: string[] = []
	if (search) active.push(`the search "${search}"`)
	if (typeSelection.length > 0) {
		active.push(`type ${typeSelection.map((type) => ARTICLE_TYPE_LABELS[type]).join(' or ')}`)
	}
	if (statusSelection.length > 0) {
		active.push(`status ${statusSelection.map((status) => ARTICLE_STATUS_LABELS[status]).join(' or ')}`)
	}
	const axisCount = Object.values(classification).filter((codes) => (codes ?? []).length > 0).length
	if (axisCount > 0) active.push(`${axisCount} classification filter${axisCount === 1 ? '' : 's'}`)
	return active
}

/** The list request for a filter state. An empty selection is omitted, never sent empty. */
function toListParams({
	search,
	typeSelection,
	statusSelection,
	classification,
	sort,
}: FilterState): ArticleListParams {
	return {
		search: search || undefined,
		articleType: typeSelection.length > 0 ? typeSelection : undefined,
		status: statusSelection.length > 0 ? statusSelection : undefined,
		classification,
		sort,
		// Title reads ascending; every date reads newest-first.
		order: sort === 'title' ? 'asc' : 'desc',
		// The endpoint's maximum, so the single page the index shows carries as much
		// as one request can rather than the default 20.
		limit: ARTICLE_LIST_PAGE_SIZE * 5,
	}
}

/**
 * The axis groups the rail shows, and the values selected on them.
 *
 * One hook rather than two pieces of state because they are coupled in one
 * direction: hiding a group has to clear its selection, or the list would keep
 * filtering on a control the user can no longer see, let alone undo.
 */
function useAxisGroups() {
	const [classification, setClassification] = useState<AxisSelection>({})
	const [shownAxisCodes, setShownAxisCodes] = useState<string[]>(DEFAULT_ARTICLE_FILTER_AXES)

	const removeAxis = useCallback((axisCode: string) => {
		setShownAxisCodes((current) => current.filter((code) => code !== axisCode))
		setClassification((current) => {
			const { [axisCode]: _removed, ...rest } = current
			return rest
		})
	}, [])

	return {
		classification,
		toggleClassification: (axisCode: string, valueCode: string) =>
			setClassification((current) => toggleFilterValue(current, axisCode, valueCode)),
		shownAxisCodes,
		addAxis: (axisCode: string) =>
			setShownAxisCodes((current) => (current.includes(axisCode) ? current : [...current, axisCode])),
		removeAxis,
		reset: useCallback(() => {
			setClassification({})
			setShownAxisCodes(DEFAULT_ARTICLE_FILTER_AXES)
		}, []),
	}
}

/**
 * The index's filter state, and the two things it derives: the list request, and
 * whether anything is narrowing it.
 *
 * That second one is why this is a hook rather than four `useState` calls in the
 * page. An empty result is a FILTERED empty exactly when something is active, and
 * the API cannot tell the page which of the two it is — both are one 204. Deriving
 * it here keeps the answer in the same place as the state it depends on.
 */
export function useArticleIndexFilters() {
	const [search, setSearch] = useState('')
	const [sort, setSort] = useState<ArticleSort>('updatedAt')
	const [typeSelection, setTypeSelection] = useState<readonly ArticleType[]>([])
	const [statusSelection, setStatusSelection] = useState<readonly ArticleStatus[]>([])
	const axisGroups = useAxisGroups()
	const { classification } = axisGroups

	const trimmedSearch = search.trim()
	const state: FilterState = { search: trimmedSearch, typeSelection, statusSelection, classification, sort }

	const activeFilters = useMemo(
		() => describeActiveFilters(state),
		[trimmedSearch, typeSelection, statusSelection, classification, sort],
	)
	const params = useMemo(
		() => toListParams(state),
		[trimmedSearch, typeSelection, statusSelection, classification, sort],
	)

	const clearAll = useCallback(() => {
		setSearch('')
		setTypeSelection([])
		setStatusSelection([])
		axisGroups.reset()
	}, [axisGroups.reset])

	return {
		...axisGroups,
		search,
		setSearch,
		sort,
		setSort,
		typeSelection,
		toggleType: (articleType: ArticleType) => setTypeSelection((current) => toggled(current, articleType)),
		statusSelection,
		toggleStatus: (status: ArticleStatus) => setStatusSelection((current) => toggled(current, status)),
		params,
		activeFilters,
		hasActiveFilters: activeFilters.length > 0,
		clearAll,
	}
}
